import { EMRdetailsProps, IEMRUpdatePayloadProps } from "@/api_framework/api_modals/EMRs";
import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useConfigurationCityListAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationCityListAPI";
import { useConfigurationStaffClinicBasedOnCityAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationStaffClinicBasedOnCityAPI";
import { useUpdateTempEMRAPI } from "@/app/hooks/api_hooks/emr/useUpdateTempEMRAPI";
import { useOrgClinicProvidersAPI } from "@/app/hooks/api_hooks/org/useOrgClinicProvidersAPI";
import { useOrgServicesByFilterAPI } from "@/app/hooks/api_hooks/org/useOrgServicesByFilterAPI";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useUtils } from "@/app/hooks/useUtils";
import {
  buildBookingServiceMaps,
  findDefaultSelectedSubServiceItem,
  resolveSubServiceNameFromSelection,
} from "@/app/utils/bookingServiceSelection";
import { IConfigurationListState } from "@/app_redux/reducers/slices/auth/configuration_slice";
import { ICustomerEMRState } from "@/app_redux/reducers/slices/auth/customer_emr_slice";
import { format } from "date-fns";
import { debounce } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";

interface NextComponentProps {
  suggested_services: string[] | string;
  surgery_name: string;
  surgery_recommendations: string;
  ipd_recommendations: string;
  surgery_date: string;
  refer_city_id: string;
  refer_clinic_id: string;
  refer_staff_id: string;
  refer_staff_name: string;
  refer_to_reason: string;
  refer_city_name: string;
  refer_clinic_name: string;
}

const NextStepsComponent = ({ emrDetails }: EMRdetailsProps) => {
  const { execute: updateEmrAPI } = useUpdateTempEMRAPI();
  const isVideoConsultation = emrDetails?.emr_type === "Video Consultation";
  const [isSurgeryChecked, setIsSurgeryChecked] = useState(false);
  const [isIpdChecked, setIsIpdChecked] = useState(false);

  const params = new URLSearchParams(location.search);
  const emr_id = params.get("emr_id");
  const emr_status = params.get("emr_status");

  const formHook = useForm<NextComponentProps>({
    mode: "onChange",
    defaultValues: {
      suggested_services: null,
      surgery_name: null,
      surgery_recommendations: null,
      ipd_recommendations: null,
      surgery_date: null,
      refer_city_id: null,
      refer_clinic_id: null,
      refer_staff_id: null,
      refer_to_reason: null,
      refer_city_name: null,
      refer_clinic_name: null,
    },
  });

  const { getCurrentDate } = useUtils();
  const { getRouteKey } = useRouter();

  const fetchOrgCities = useConfigurationCityListAPI();
  const fetchOrgClinicProviders = useOrgClinicProvidersAPI("EMR");
  const { clinicsByCities, execute: fetchStaffClinicBasedOnCity } = useConfigurationStaffClinicBasedOnCityAPI();
  const { serviceTypes, execute: fetchOrgServicesByFilter } = useOrgServicesByFilterAPI();
  const didFetchNextStepsServices = useRef(false);
  const { cityList } = useRouteInfo(getRouteKey("CONFIGURATION", "id")).routeState.state as IConfigurationListState;
  const { providers } = useRouteInfo(getRouteKey("CUSTOMER_EMR", "id")).routeState.state as ICustomerEMRState;

  const bookingServiceMaps = useMemo(() => buildBookingServiceMaps(serviceTypes ?? []), [serviceTypes]);
  const subServiceOptions = useMemo(() => {
    return Object.values(bookingServiceMaps.subServiceByIdMap).map((subService) => ({
      id: subService.sub_service_id,
      name: subService.sub_service_name,
    }));
  }, [bookingServiceMaps.subServiceByIdMap]);
  const subServiceSelectItems = useSelectMenuReducer(subServiceOptions, "name", "id");
  const citySelectItems = useSelectMenuReducer(cityList, "name", "id");
  const clinicSelectItems = useSelectMenuReducer(clinicsByCities, "clinic_name", "clinic_id");
  const providerSelectItems = useSelectMenuReducer(providers, "name", "staff_id");
  const suggestedServicesValue = formHook.watch("suggested_services");
  const defaultSelectedSuggestedService = useMemo(
    () => findDefaultSelectedSubServiceItem(suggestedServicesValue, subServiceSelectItems),
    [suggestedServicesValue, subServiceSelectItems],
  );

  // Debounced API function
  const debouncedUpdate = useMemo(
    () =>
      debounce((data) => {
        const payload: IEMRUpdatePayloadProps = {
          next_steps: {
            suggested_services: resolveSubServiceNameFromSelection(data.suggested_services, {
              maps: bookingServiceMaps,
              selectItems: subServiceSelectItems,
            }),
            surgery_name: data.surgery_name,
            surgery_recommendations: data.surgery_recommendations,
            ipd_recommendations: data.ipd_recommendations,
            surgery_date: data.surgery_date,
            refer_city_id: data.refer_city_id,
            refer_clinic_id: data.refer_clinic_id,
            refer_staff_id: data.refer_staff_id,
            refer_staff_name: data.refer_staff_name,
            refer_to_reason: data.refer_to_reason,
            refer_city_name: data.refer_city_name,
            refer_clinic_name: data.refer_clinic_name,
          },
          emr_status: emrDetails?.emr_status ?? emr_status,
        };
        if (JSON.stringify(data) === JSON.stringify(emrDetails?.next_steps)) return;
        updateEmrAPI(emr_id, payload);
      }, 1000),
    [emr_id, emrDetails?.emr_status, bookingServiceMaps, subServiceSelectItems],
  );

  const isFirstLoad = useRef(false);

  useEffect(() => {
    const subscription = formHook?.watch((value) => {
      if (!isFirstLoad?.current) return;
      debouncedUpdate(value as NextComponentProps);
    });

    return () => subscription.unsubscribe();
  }, [formHook, debouncedUpdate]);

  useEffect(() => {
    if (emrDetails) {
      isFirstLoad.current = false;
      formHook.reset(emrDetails?.next_steps);
      setIsSurgeryChecked(
        Boolean(emrDetails?.next_steps?.surgery_recommendations || emrDetails?.next_steps?.surgery_name),
      );
      setIsIpdChecked(Boolean(emrDetails?.next_steps?.ipd_recommendations));
      setTimeout(() => {
        isFirstLoad.current = true;
      }, 0);
    }
  }, [emrDetails]);

  useEffect(() => {
    fetchOrgCities();
  }, []);

  useEffect(() => {
    if (didFetchNextStepsServices.current) return;
    didFetchNextStepsServices.current = true;
    fetchOrgServicesByFilter({
      filterType: "emr_next_steps",
    });
  }, [fetchOrgServicesByFilter]);

  useEffect(() => {
    const city = formHook?.getValues("refer_city_id");
    if (city) {
      fetchStaffClinicBasedOnCity([city]);
    }
  }, [formHook.watch("refer_city_id")]);

  useEffect(() => {
    const clinicId = formHook.watch("refer_clinic_id");
    if (clinicId) {
      fetchOrgClinicProviders({
        ROTADate: getCurrentDate(),
        clinicIds: [clinicId],
      });
    }
  }, [formHook.watch("refer_clinic_id")]);

  return (
    <>
      <div className="w-full bg-white p-2 rounded-[10px] mt-3">
        <h3 className="text-[#656565] text-sm font-medium tracking-tight42 px-2">Next Steps (Surgery)</h3>
        {isVideoConsultation ? (
          <div className="bg-[#F9FAFB] rounded-xl p-3 mt-2">
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={isSurgeryChecked}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setIsSurgeryChecked(checked);
                  if (!checked) {
                    formHook.setValue("surgery_recommendations", "");
                    debouncedUpdate({ ...formHook.getValues(), surgery_recommendations: "" });
                  }
                }}
                className="h-4 w-4 rounded border-gray-300 text-black cursor-pointer"
              />
              <span className="text-[#000000] text-sm font-medium">Surgery</span>
            </label>
            <div className="bg-white rounded-xl p-[10px] mt-2">
              <textarea
                rows={2}
                disabled={!isSurgeryChecked}
                className={`w-full border rounded-xl p-3 text-sm focus:outline-none transition-colors ${
                  !isSurgeryChecked
                    ? "bg-[#F9FAFB] text-gray-400 border-[#E2E2E2] cursor-not-allowed placeholder-gray-400"
                    : "bg-white text-black border-[#E2E2E2] placeholder-[#6C727F]"
                }`}
                placeholder="Enter Surgery Details"
                {...formHook.register("surgery_recommendations")}
              ></textarea>
            </div>
          </div>
        ) : (
          <div className="bg-[#F9FAFB] rounded-lg p-2 mt-2">
            <div className="bg-white p-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-0">
              <h3 className="w-full sm:w-1/4 shrink-0 text-[#000000] text-sm font-medium tracking-tight42">Procedure Date</h3>
              <div className="flex w-full flex-wrap items-center gap-2 sm:w-48 sm:shrink-0 sm:flex-nowrap sm:gap-4">
                <PWLCalendarComponent
                  showFutureYears
                  allowPastDaySelection={false}
                  errorMessages={[{ message: "Date is required", type: "required" }]}
                  register={formHook.register("surgery_date", {
                    required: true,
                  })}
                  showTypedErrors={false}
                  disableDatesBeforeDate={getCurrentDate()}
                  showLeftNav={false}
                  fieldError={formHook.formState.errors.surgery_date}
                  onSelectDate={(date) => {
                    formHook.setValue("surgery_date", format(date, "yyyy-MM-dd"));
                  }}
                  defaultSelectedDate={formHook.watch("surgery_date")}
                  disableDialogScrollLock
                  className="h-9 w-full"
                />
              </div>
            </div>
            <div className="bg-white p-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-0">
              <h3 className="w-full sm:w-1/4 shrink-0 text-[#000000] text-sm font-medium tracking-tight42">Recommended Procedure</h3>
              <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-nowrap sm:gap-4">
                <SearchableSelectMenu
                  errorMessages={[{ message: "", type: "required" }]}
                  onSelectItem={(item) => {
                    if (item) {
                      formHook.setValue("suggested_services", [item.id]);
                    }
                  }}
                  fieldError={formHook?.formState?.errors?.suggested_services}
                  register={formHook.register(`suggested_services`, { required: true })}
                  selectItems={subServiceSelectItems}
                  placeholder="Select Service"
                  showTooltips={true}
                  showTypedErrors={true}
                  showDropdownIcon={true}
                  defaultSelected={defaultSelectedSuggestedService}
                  listBoxClassName="w-full"
                  className="text-[#000000]"
                  containerClassName="w-full sm:w-72"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {isVideoConsultation && (
        <div className="w-full bg-white p-2 rounded-[10px] mt-3">
          <h3 className="text-[#656565] text-sm font-medium tracking-tight42 px-2">Refer to Other CLinic (IPD)</h3>
          <div className="bg-[#F9FAFB] rounded-xl p-3 mt-2">
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={isIpdChecked}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setIsIpdChecked(checked);
                  if (!checked) {
                    formHook.setValue("ipd_recommendations", "");
                    debouncedUpdate({ ...formHook.getValues(), ipd_recommendations: "" });
                  }
                }}
                className="h-4 w-4 rounded border-gray-300 text-black cursor-pointer"
              />
              <span className="text-[#000000] text-sm font-medium">IPD</span>
            </label>
            <div className="bg-white rounded-xl p-[10px] mt-2">
              <textarea
                rows={2}
                disabled={!isIpdChecked}
                className={`w-full border rounded-xl p-3 text-sm focus:outline-none transition-colors ${
                  !isIpdChecked
                    ? "bg-[#F9FAFB] text-gray-400 border-[#E2E2E2] cursor-not-allowed placeholder-gray-400"
                    : "bg-white text-black border-[#E2E2E2] placeholder-[#6C727F]"
                }`}
                placeholder="Enter IPD Details"
                {...formHook.register("ipd_recommendations")}
              ></textarea>
            </div>
          </div>
        </div>
      )}

      {!isVideoConsultation && (
        <div className="w-full  bg-white p-2 rounded-[10px] mt-3">
          <div className="bg-white  p-2  ">
            <h3 className="text-[#656565] text-sm font-medium tracking-tight42 px-2">Refer to other Vetic Clinic (IPD)</h3>
            <div className="flex flex-col gap-3 px-2 mt-3 sm:flex-row sm:gap-4 sm:items-center">
              <SearchableSelectMenu
                errorMessages={[{ message: "", type: "required" }]}
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue("refer_city_id", item.id);
                    formHook.setValue("refer_city_name", item.title);
                  }
                }}
                fieldError={formHook?.formState?.errors?.refer_city_id}
                register={formHook.register(`refer_city_id`, { required: true })}
                selectItems={citySelectItems}
                placeholder="Select City"
                showTooltips={true}
                showTypedErrors={true}
                showDropdownIcon={true}
                defaultSelected={citySelectItems?.filter((oc) => oc.id === formHook.watch(`refer_city_id`))[0]}
                listBoxClassName="w-full"
                className="text-[#000000]"
                containerClassName="w-full sm:w-72"
              />
              <SearchableSelectMenu
                errorMessages={[{ message: "", type: "required" }]}
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue("refer_clinic_id", item.id);
                    formHook.setValue("refer_clinic_name", item.title);
                  }
                }}
                fieldError={formHook?.formState?.errors?.refer_clinic_id}
                register={formHook.register(`refer_clinic_id`, { required: true })}
                selectItems={clinicSelectItems}
                placeholder="Select Clinic"
                showTooltips={true}
                showTypedErrors={true}
                showDropdownIcon={true}
                defaultSelected={clinicSelectItems?.filter((oc) => oc.id === formHook.watch(`refer_clinic_id`))[0]}
                listBoxClassName="w-full"
                className="text-[#000000]"
                containerClassName="w-full sm:w-72"
              />
              <SearchableSelectMenu
                errorMessages={[{ message: "", type: "required" }]}
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue("refer_staff_id", item.id);
                    formHook.setValue("refer_staff_name", item.title);
                  }
                }}
                fieldError={formHook?.formState?.errors?.refer_staff_id}
                register={formHook.register(`refer_staff_id`, { required: false })}
                selectItems={providerSelectItems}
                placeholder="Select Provider"
                showTooltips={true}
                showTypedErrors={true}
                showDropdownIcon={true}
                defaultSelected={providerSelectItems?.filter((oc) => oc.id === formHook.watch(`refer_staff_id`))[0]}
                listBoxClassName="w-full"
                className="text-[#000000]"
                containerClassName="w-full sm:w-72"
              />
            </div>
          </div>

          <div className="bg-[#FFFFFF]  p-[10px]   px-4">
            <textarea
              className="w-full   border border-[#E2E2E2] rounded-xl p-3 text-sm text-[#6C727F] focus:outline-none"
              placeholder="Write Refer reason"
              {...formHook.register("refer_to_reason", {
                required: false,
              })}
            ></textarea>
          </div>
        </div>
      )}
    </>
  );
};
export default NextStepsComponent;
